/**
 * CockpitDeliberation — Layout 3 colonnes asymétriques (desktop)
 * + stack vertical mobile avec drawer sources.
 *
 * Style : Qorum & The Seven v3.1 — noir / blanc / or discret.
 * Composé au-dessus de useDeliberation (intact) + useResearchSources (séparé).
 *
 * Phase 2-bis (Sprint S31) — 6 UX fixes :
 *   1. Input "Intervenir dans le débat" pendant phase debate/converge
 *   2. CTA "Demander le Mémo du Conseil" à la fin du débat
 *   3. Mini avatar N&B circulaire à gauche de chaque bulle
 *   4. Style WhatsApp : marge gauche, ombre subtile, groupage consécutif
 *   5. Thumbnails YouTube/social dans la colonne sources
 *   6. Halo doré pulsé sur portrait du cadre actif
 */
import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";

const BUBBLE_SANITIZE = { ALLOWED_TAGS: ["em", "strong", "p", "br", "a"], ALLOWED_ATTR: ["href", "target", "rel"] };
const sanitizeBubbleText = (s: string) => DOMPurify.sanitize(s ?? "", BUBBLE_SANITIZE);
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import type { DeliberationHookResult } from "@/hooks/use-deliberation";
import type { ResearchSourcesResult, PlatformBucket } from "@/hooks/use-research-sources";
import type { InvestigationSource } from "@/components/Deliberation/InvestigationLive";
import { COUNCIL } from "@/lib/council";
import { apiFetch } from "@/lib/api";
import { NanaeLogoFull, NanaeLogoSymbol } from "@/components/qv3/NanaeLogo";
import { QLiveDot } from "@/components/qv3/QLiveDot";
import { RelaunchComposer } from "@/components/qv3/RelaunchComposer";
import { ConversationAttachmentButton } from "@/components/qorum/ConversationAttachmentButton";
import { VerdictEndBlock } from "@/components/Verdict/VerdictEndBlock";
import { isGuestKey } from "@/lib/guest-pool";
import type { GuestInfo } from "@/lib/guest-pool";
import { PERSONA_TO_MODEL } from "@/lib/persona-mapping";
import { PersonaResearchWindow } from "@/components/qv3/PersonaResearchWindow";


import pDelphine from "@/assets/landing-v3/p-delphine.jpg";
import pHenri from "@/assets/landing-v3/p-henri.jpg";
import pEva from "@/assets/landing-v3/p-eva.jpg";
import pAkilah from "@/assets/landing-v3/p-akilah.jpg";
import pJulien from "@/assets/landing-v3/p-julien.jpg";
import pRen from "@/assets/landing-v3/p-ren.jpg";
import pImani from "@/assets/landing-v3/p-imani.jpg";
import { PersonaProvider } from "@/components/qorum/PersonaProvider";

const PORTRAITS: Record<string, string> = {
  delphine: pDelphine,
  henri: pHenri,
  eva: pEva,
  akilah: pAkilah,
  julien: pJulien,
  ren: pRen,
  imani: pImani,
};

const COUNCIL_BY_ID = Object.fromEntries(COUNCIL.map((c) => [c.id, c]));
const TURN_ORDER_IDS = ["henri", "delphine", "julien", "akilah", "eva", "ren", "imani"];

/** Portrait + identité d'affichage pour un message — gère 7 du Conseil + invités. */
function resolveSpeaker(personaKey: string, guests: Record<string, GuestInfo>) {
  if (isGuestKey(personaKey)) {
    const g = guests[personaKey];
    if (g) {
      const roleLabel = g.domain ? `Invité · ${g.domain}` : "Invité";
      return {
        kind: "guest" as const,
        portrait: g.portrait,
        name: g.title,
        role: roleLabel,
        accent: g.colorHex,
      };
    }
    return { kind: "guest" as const, portrait: undefined, name: "Invité", role: "Invité", accent: undefined };
  }
  const c = COUNCIL_BY_ID[personaKey];
  return {
    kind: "council" as const,
    portrait: PORTRAITS[personaKey],
    name: c?.name ?? personaKey,
    role: c?.role,
    accent: undefined as string | undefined,
  };
}

/** Affichage temps mm:ss à partir d'un nb de secondes. */
function fmtTime(totalSec: number) {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function actLabel(phase: string) {
  switch (phase) {
    case "summon":
      return "ACTE I · CONVOCATION";
    case "debate":
      return "ACTE II · TOUR DE TABLE";
    case "converge":
      return "ACTE III · CONVERGENCE";
    case "verdict":
      return "ACTE IV · VERDICT";
    default:
      return "ACTE I";
  }
}

/** Best-effort YouTube thumbnail extraction depuis l'URL. */
function youtubeThumb(url: string): string | null {
  try {
    const u = new URL(url);
    let id: string | null = null;
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.replace(/^\//, "");
    } else if (u.searchParams.has("v")) {
      id = u.searchParams.get("v");
    } else if (u.pathname.startsWith("/embed/") || u.pathname.startsWith("/shorts/")) {
      id = u.pathname.split("/")[2] ?? null;
    }
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
  } catch {
    return null;
  }
}

interface Props {
  state: DeliberationHookResult;
  sources: ResearchSourcesResult;
}

export function CockpitDeliberation({ state, sources }: Props) {
  const navigate = useNavigate();
  const messages = state.displayedMessages;
  // Snapshot du nombre de messages au moment où le débat s'achève la 1ère fois.
  // Tout message reçu APRÈS (via Relancer le Conseil) est rendu sous le cadre de relance.
  const [relaunchSplit, setRelaunchSplit] = useState<number | null>(null);
  const streamRef = useRef<HTMLDivElement | null>(null);
  // Si la délib est déjà terminée à l'ouverture (rejeu), on ancre en haut
  // (lecture depuis la question). Sinon, on suit le bas en live.
  const wasCompletedAtMountRef = useRef<boolean>(state.isCompleted || state.meetingDone);
  const stickToBottomRef = useRef(!wasCompletedAtMountRef.current);
  const [elapsed, setElapsed] = useState(0);
  const [mobileSourcesOpen, setMobileSourcesOpen] = useState(false);
  const [interventionText, setInterventionText] = useState("");
  const [intervening, setIntervening] = useState(false);


  // Chrono live (1s)
  useEffect(() => {
    if (state.isCompleted || state.meetingDone) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [state.isCompleted, state.meetingDone]);

  // FIX 3 — Auto-scroll smart : suit le bas SAUF si l'user a remonté.
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    // Ancrage initial : si la saisine est ouverte en mode "rejeu" (déjà
    // terminée au mount), on positionne le scroll en haut sur la question.
    if (wasCompletedAtMountRef.current) {
      el.scrollTo({ top: 0, behavior: "auto" });
    }
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distance < 200;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const lastText = messages[messages.length - 1]?.text ?? "";
  useEffect(() => {
    const el = streamRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages.length, lastText]);

  // Quand le débat vient de se terminer, le VerdictEndBlock (loader d'attente
  // du verdict) apparaît sous le dernier message. Sans nouveau message, le
  // useEffect ci-dessus ne se re-déclenche pas → l'autoscroll s'arrête et
  // l'utilisateur ne voit pas l'animation. On force le rattrapage ici, en
  // ré-activant stickToBottom et en re-scrollant après le mount du bloc.
  const debateFinishedScroll = state.isCompleted || state.meetingDone;
  useEffect(() => {
    if (!debateFinishedScroll) return;
    const el = streamRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    const scrollDown = () => el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    scrollDown();
    const t1 = window.setTimeout(scrollDown, 120);
    const t2 = window.setTimeout(scrollDown, 600);
    const t3 = window.setTimeout(scrollDown, 1500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [debateFinishedScroll]);

  // Cadre actif : dérivé du dernier message reçu OU d'un bubble typing/streaming.
  // En SSE live, le backend pose souvent "complete" sans phase typing → on suit
  // le dernier persona ayant parlé et on garde son halo allumé 2.2s.
  const [recentSpeaker, setRecentSpeaker] = useState<string | null>(null);
  useEffect(() => {
    const last = state.messages[state.messages.length - 1];
    if (!last) return;
    const p = String(last.persona);
    if (p === "user" || p === "president") return;
    setRecentSpeaker(p);
    const tid = window.setTimeout(() => {
      setRecentSpeaker((cur) => (cur === p ? null : cur));
    }, 2200);
    return () => window.clearTimeout(tid);
  }, [state.messages.length]);

  const activeIds = useMemo(() => {
    const set = new Set<string>();
    for (const b of state.bubbles) {
      if (b.state === "typing" || b.state === "streaming") set.add(b.persona);
    }
    if (recentSpeaker) set.add(recentSpeaker);
    return set;
  }, [state.bubbles, recentSpeaker]);


  // Métriques footer
  const confidence = useMemo(() => {
    const vals = state.messages
      .map((m) => m.trustworthiness)
      .filter((v): v is number => typeof v === "number" && v > 0);
    if (vals.length === 0) return null;
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    return avg.toFixed(1);
  }, [state.messages]);

  const contestations = useMemo(
    () => state.messages.filter((m) => (m.round ?? 0) >= 2).length,
    [state.messages],
  );

  // ============================================================
  // FIX 1 — Visibilité input intervention :
  // pendant phase debate OU converge, débat actif (pas fini, pas verdict).
  // ============================================================
  // Détection renforcée : tout signal local d'activité (message OU bulle
  // en streaming/typing, connexion live + phase active, round qui monte)
  // bascule debateActive=true de façon synchrone, sans attendre une
  // mise à jour de phase potentiellement retardée par la latence réseau.
  const hasStreamingMessage = useMemo(
    () => state.messages.some((m) => m.state === "streaming" || m.state === "typing"),
    [state.messages],
  );

  const hasActiveBubble = useMemo(
    () =>
      state.bubbles.some(
        (b) => b.state === "streaming" || b.state === "typing" || b.state === "active",
      ),
    [state.bubbles],
  );

  // Round qui progresse = nouveau débat en cours (ex: relance ACTE II).
  // Ref synchrone : mise à jour immédiate à la première trame reçue.
  const lastSeenRoundRef = useRef(0);
  const roundProgressing = useMemo(() => {
    const maxRound = state.messages.reduce(
      (acc, m) => (typeof m.round === "number" && m.round > acc ? m.round : acc),
      0,
    );
    const progressing = maxRound > lastSeenRoundRef.current && !state.verdictReady;
    if (maxRound > lastSeenRoundRef.current) lastSeenRoundRef.current = maxRound;
    if (state.verdictReady || state.isCompleted || state.meetingDone) {
      lastSeenRoundRef.current = maxRound;
    }
    return progressing;
  }, [state.messages, state.verdictReady, state.isCompleted, state.meetingDone]);

  const phaseActive =
    state.currentPhase === "summon" ||
    state.currentPhase === "debate" ||
    state.currentPhase === "converge";

  const debateActive =
    !state.isCompleted &&
    !state.meetingDone &&
    !state.verdictReady &&
    (phaseActive ||
      hasStreamingMessage ||
      hasActiveBubble ||
      roundProgressing ||
      (state.connectionState === "live" && state.currentPhase !== "verdict"));

  // ============================================================
  // FIX 2 — Fin de débat : afficher CTA "Demander le Mémo".
  // Dès qu'un débat redevient actif (streaming, summon, debate, converge),
  // le bloc de fin est masqué instantanément.
  // ============================================================
  // S31 strict gating : le CTA "Voir le verdict" ne se révèle QUE sur l'event
  // SSE `verdict` (verdictReady=true). On ignore `complete` qui arrive ~150 s
  // plus tard et `isCompleted` (initial fetch) pour éviter les faux positifs.
  const debateFinished = !debateActive && state.verdictReady;

  // Au moment où le débat s'achève la 1ère fois, on fige la position du cadre de relance
  // dans le flux. Les bulles arrivant ensuite (via relance) viennent SOUS le cadre.
  useEffect(() => {
    if (debateFinished && relaunchSplit === null) {
      setRelaunchSplit(messages.length);
    }
  }, [debateFinished, relaunchSplit, messages.length]);



  // Recherche terminée : soit le pipeline backend l'a annoncé, soit la délibération est close.
  const researchDone = sources.isCompleted || state.isCompleted || state.meetingDone;

  async function handleIntervene() {
    const text = interventionText.trim();
    if (!text || intervening) return;
    setIntervening(true);
    try {
      await apiFetch(`/api/chat/${state.id}/intervene`, {
        method: "POST",
        body: JSON.stringify({ message: text }),
      });
      setInterventionText("");
      toast.success("Votre intervention a été transmise au Conseil.");
    } catch (e) {
      toast.error("Impossible de transmettre votre intervention pour l'instant.");
    } finally {
      setIntervening(false);
    }
  }

  function handleAskMemo() {
    const vid = state.verdictId ?? state.id;
    navigate({ to: "/verdict/$id", params: { id: vid } });
  }




  return (
    <div className="qv3 qv3-dark qv3-cockpit">
      {/* ===== Header — Hōjin ===== */}
      <header className="qv3-cockpit-header">
        <div className="qv3-cockpit-brand">
          <Link to="/foyer" aria-label="Retour au Bureau" style={{ display: "inline-flex", textDecoration: "none", color: "inherit" }}>
            <NanaeLogoFull layout="horizontal" symbolSize="md" wordmarkSize="md" withKanji={false} />
          </Link>
        </div>
        <div className="qv3-cockpit-title">
          Le Conseil{" "}
          <span
            className="qv3-italic-gold"
            aria-label={debateFinished ? "Saisine terminée" : "Saisine en direct"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 4,
              padding: "2px 8px",
              border: `1px solid ${debateFinished ? "rgba(255,23,68,0.45)" : "rgba(220,80,80,0.55)"}`,
              borderRadius: 999,
              fontFamily: '"JetBrains Mono", monospace',
              fontStyle: "normal",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: debateFinished ? "#F5EFE3" : "#dc5050",
            }}
          >
            {!debateFinished && (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#dc5050",
                  boxShadow: "0 0 6px #dc5050",
                }}
              />
            )}
            {debateFinished ? "Terminée" : "Live"}
          </span>
        </div>
        <div className="qv3-cockpit-meta">
          {!debateFinished ? (
            <span className="qv3-live-pill" aria-label="Délibération en cours">
              <QLiveDot label="LIVE" />
              <span className="qv3-live-pill-sep" aria-hidden>·</span>
              <span className="qv3-mono-hud is-caps size-xs qv3-live-pill-text">
                {actLabel(state.currentPhase)} · {fmtTime(elapsed)} / ~3:30
              </span>
            </span>
          ) : (
            <span className="qv3-mono-hud is-caps size-xs" style={{ whiteSpace: "nowrap" }}>
              {actLabel(state.currentPhase)} · {fmtTime(elapsed)} / ~3:30
            </span>
          )}
          <Link
            to="/foyer"
            className="qv3-mono-hud is-caps size-xs qv3-cockpit-new-link"
          >
            ← Nouvelle saisine
          </Link>
        </div>
      </header>

      {state.parentConversationId && (
        <div
          role="note"
          aria-label="Suite de votre saisine initiale"
          style={{
            padding: "10px 20px",
            background: "#000000",
            borderBottom: "1px solid rgba(0, 229, 255, 0.35)",
            color: "#F5EFE3",
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ opacity: 0.72 }}>Suite de votre saisine initiale</span>
          <span aria-hidden style={{ opacity: 0.4 }}>·</span>
          <Link
            to="/deliberation/$id"
            params={{ id: state.parentConversationId }}
            style={{
              color: "#00E5FF",
              textDecoration: "none",
              borderBottom: "1px solid rgba(0, 229, 255, 0.6)",
              paddingBottom: 1,
            }}
          >
            Voir le débat et le mémo d'origine →
          </Link>
        </div>
      )}


      {/* ===== Layout 3-col / stack ===== */}
      <div className="qv3-cockpit-grid">
        {/* — Colonne gauche — Cadres */}
        <aside className="qv3-cockpit-cadres" aria-label="Les Sept">
          <p
            className="qv3-cockpit-aside-claim"
            style={{
              margin: "0 0 12px",
              fontFamily: '"Playfair Display", serif',
              fontStyle: "italic",
              fontSize: 12.5,
              lineHeight: 1.45,
              color: "#F5EFE3",
              maxWidth: 260,
            }}
          >
            Sept cadres, sept modèles d'IA distincts — la diversité des intelligences fait la robustesse du verdict.
          </p>

          <ul className="qv3-cadre-list">
            {TURN_ORDER_IDS.map((id) => {
              const c = COUNCIL_BY_ID[id];
              if (!c) return null;
              const isActive = activeIds.has(id);
              return (
                <li key={id} className={`qv3-cadre${isActive ? " is-active" : ""}`}>
                  <div className="qv3-cadre-photo">
                    <img src={PORTRAITS[id]} alt={`Portrait de ${c.name}`} loading="lazy" />
                  </div>
                  <div className="qv3-cadre-text">
                     <span className="qv3-cadre-name">{c.name}</span>
                     <span className="qv3-cadre-role">{c.role}</span>
                     <PersonaProvider
                       personaId={id}
                       align="start"
                       fontSize={9}
                       logoSize={11}
                       color="#F5EFE3"
                       style={{ fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace', opacity: 1 }}
                     />
                   </div>
                  <span
                    aria-hidden="true"
                    className={isActive ? "qv3-dot-active" : "qv3-dot-idle"}
                  />
                </li>
              );
            })}
          </ul>

          {/* Invités du tour (8e siège) — convives, pas cadres permanents. */}
          {Object.values(state.guests).length > 0 && (
            <ul className="qv3-cadre-list qv3-cadre-guests" aria-label="Invité du Conseil">
              {Object.values(state.guests).map((g) => {
                const isActive = activeIds.has(g.key);
                const roleLine = g.domain ? `Invité · ${g.domain}` : "Invité";
                return (
                  <li
                    key={g.key}
                    className={`qv3-cadre qv3-cadre-guest${isActive ? " is-active" : ""}`}
                    data-testid={`guest-cadre-${g.key}`}
                    style={g.colorHex ? ({ ["--qv3-guest-accent" as string]: g.colorHex } as React.CSSProperties) : undefined}
                  >
                    <div className="qv3-cadre-photo qv3-cadre-photo-guest">
                      <img src={g.portrait} alt={`Portrait de l'invité ${g.title}`} loading="lazy" />
                      <span className="qv3-cadre-guest-badge" aria-hidden="true">Invité</span>
                    </div>
                    <div className="qv3-cadre-text">
                      <span className="qv3-cadre-name">{g.title}</span>
                      <span className="qv3-cadre-role">{roleLine}</span>
                    </div>
                    <span
                      aria-hidden="true"
                      className={isActive ? "qv3-dot-active" : "qv3-dot-idle"}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {/* Mobile : ligne horizontale de dots seulement (cadres réduits) */}
          <div className="qv3-cadre-dots-mobile" aria-hidden="true">
            {TURN_ORDER_IDS.map((id) => (
              <span
                key={id}
                className={activeIds.has(id) ? "qv3-dot-active" : "qv3-dot-idle"}
              />
            ))}
            {Object.values(state.guests).map((g) => (
              <span
                key={g.key}
                className={`${activeIds.has(g.key) ? "qv3-dot-active" : "qv3-dot-idle"} qv3-dot-guest`}
              />
            ))}
          </div>
        </aside>

        {/* — Colonne centrale — Stream bulles */}
        <main className="qv3-cockpit-stream" ref={streamRef}>
          {messages.length === 0 ? (
            <CouncilWaiting />
          ) : (() => {
            const ctx = { councilIdx: -1, seenPersonas: new Set<string>() };
            const renderRow = (m: typeof messages[number], idx: number, arr: typeof messages) => {
              const personaStr = String(m.persona);
              const c = COUNCIL_BY_ID[personaStr];
              const prev = idx > 0 ? arr[idx - 1] : null;
              const groupedWithPrev = prev?.persona === m.persona;
              const isUser = personaStr === "user" || personaStr === "president";
              const isGuest = isGuestKey(personaStr);
              if (!isUser) ctx.councilIdx += 1;
              if (m.abstained) {
                const displayName = c?.name ?? personaStr;
                return (
                  <li
                    key={m.id}
                    className="qv3-bubble-row qv3-abstention"
                    data-testid={`abstention-${personaStr}`}
                    aria-label={`${displayName} s'est abstenu ce tour`}
                    style={{
                      justifyContent: "center",
                      opacity: 0.55,
                      fontFamily: '"DM Mono", monospace',
                      fontSize: 11,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                      color: "var(--smoke, #8a8580)",
                      padding: "6px 0",
                    }}
                  >
                    — {displayName} s'est abstenu·e ce tour —
                  </li>
                );
              }
              const speaker = !isUser ? resolveSpeaker(personaStr, state.guests) : null;
              const sideClass = isUser
                ? " is-user is-right"
                : ctx.councilIdx % 2 === 0
                  ? " is-left"
                  : " is-right";
              const avatarOnRight = isUser || (!isUser && ctx.councilIdx % 2 === 1);
              const portraitSrc = speaker?.portrait;
              const renderAvatar = () => (
                <div
                  className={`qv3-bubble-avatar${isUser ? " qv3-bubble-avatar-user" : ""}${isGuest ? " qv3-bubble-avatar-guest" : ""}`}
                  aria-hidden="true"
                >
                  {!groupedWithPrev ? (
                    isUser ? (
                      <span>V</span>
                    ) : portraitSrc ? (
                      <img src={portraitSrc} alt="" loading="lazy" />
                    ) : null
                  ) : null}
                  {!groupedWithPrev && isGuest ? (
                    <span className="qv3-bubble-avatar-guest-badge" aria-hidden="true">
                      Invité
                    </span>
                  ) : null}
                </div>
              );
              const bubbleName = isUser
                ? "Vous"
                : speaker
                  ? `${speaker.name}${speaker.role ? ` · ${speaker.role}` : ""}`
                  : personaStr;

              let researchWindow: React.ReactNode = null;
              if (!isUser && !isGuest && !ctx.seenPersonas.has(personaStr)) {
                ctx.seenPersonas.add(personaStr);
                const modelKey =
                  PERSONA_TO_MODEL[personaStr as keyof typeof PERSONA_TO_MODEL];
                const pr =
                  (modelKey ? state.personaResearch?.[modelKey] : undefined) ??
                  state.personaResearch?.[personaStr];
                if (pr && (pr.queries.length > 0 || pr.sources.length > 0)) {
                  researchWindow = (
                    <PersonaResearchWindow
                      queries={pr.queries}
                      sources={pr.sources.map((s) => ({
                        title: s.title,
                        score: s.score,
                        url: s.url,
                      }))}
                    />
                  );
                }
              }

              return (
                <li
                  key={m.id}
                  className={`qv3-bubble-row${groupedWithPrev ? " is-grouped" : ""}${sideClass}${isGuest ? " is-guest" : ""}`}
                  data-testid={isGuest ? `guest-bubble-${personaStr}` : undefined}
                >
                  {!avatarOnRight && renderAvatar()}
                  <article className="qv3-bubble qv3-fade-in">
                    {!groupedWithPrev && (
                      <p className="qv3-bubble-name">{bubbleName}</p>
                    )}
                    <p
                      className="qv3-bubble-text"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: sanitizeBubbleText(m.text || "…") }}
                    />
                    {researchWindow}
                  </article>
                  {avatarOnRight && renderAvatar()}
                </li>
              );
            };

            const split = relaunchSplit ?? messages.length;
            const pre = messages.slice(0, split);
            const post = messages.slice(split);

            return (
              <>
                <ol className="qv3-bubble-stream">
                  {pre.map((m, idx) => renderRow(m, idx, pre))}
                </ol>

                {/* ===== Fin du débat : verdict court intégré + cadre de relance ===== */}
                {debateFinished && (
                  <div className="qv3-debate-end qv3-fade-in" role="region" aria-label="Fin de la délibération">
                    <div className="qv3-debate-end-rule">
                      <span className="qv3-mono">Le Conseil a terminé ses délibérations</span>
                    </div>
                    <VerdictEndBlock
                      conversationId={state.id}
                      branch={state.branch}
                    />
                    <RelaunchComposer parentId={state.id} />
                  </div>
                )}

                {post.length > 0 && (
                  <ol className="qv3-bubble-stream qv3-bubble-stream-relaunch" aria-label="Suite de la délibération après relance">
                    {post.map((m, idx) => renderRow(m, idx, post))}
                  </ol>
                )}
              </>
            );
          })()}



          {/* ===== FIX 1 — Input intervention ===== */}
          {debateActive && (
            <>
              <ConversationAttachmentButton conversationId={state.id} disabled={intervening} />
              <form
                className="qv3-intervene"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleIntervene();
                }}
                aria-label="Intervenir dans le débat"
              >
                <input
                  type="text"
                  value={interventionText}
                  onChange={(e) => setInterventionText(e.target.value)}
                  placeholder="Intervenir dans le débat…"
                  className="qv3-intervene-input"
                  disabled={intervening}
                  maxLength={500}
                />
                <button
                  type="submit"
                  className="qv3-btn qv3-intervene-btn"
                  disabled={intervening || !interventionText.trim()}
                >
                  {intervening ? "…" : "Envoyer"}
                </button>
              </form>
            </>
          )}
        </main>

        {/* — Colonne droite — Sources (desktop) */}
        <aside className="qv3-cockpit-sources qv3-only-desktop" aria-label="Sources de recherche">
          <SourcesPanel sources={sources} researchDone={researchDone} />
        </aside>
      </div>

      {/* ===== Footer métriques ===== */}
      <footer className="qv3-cockpit-footer">
        <span className="qv3-mono">
          Confidence: {confidence ?? "—"}{confidence ? "/10" : ""}
        </span>
        <span className="qv3-mono">Sources: {sources.total}</span>
        <span className="qv3-mono">Contestations: {contestations}</span>
        <button
          type="button"
          className="qv3-only-mobile qv3-cockpit-sources-tap"
          onClick={() => setMobileSourcesOpen(true)}
        >
          Sources ({sources.total}) →
        </button>
      </footer>

      {/* ===== Drawer mobile sources ===== */}
      {mobileSourcesOpen && (
        <div className="qv3-cockpit-drawer-overlay" onClick={() => setMobileSourcesOpen(false)}>
          <div
            className="qv3-cockpit-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Sources de recherche"
          >
            <button
              type="button"
              className="qv3-cockpit-drawer-close"
              onClick={() => setMobileSourcesOpen(false)}
              aria-label="Fermer"
            >
              ×
            </button>
            <SourcesPanel sources={sources} researchDone={researchDone} />
          </div>
        </div>
      )}

      <CockpitStyles />
    </div>
  );
}

/* ====================== Sources panel ====================== */
function SourcesPanel({ sources, researchDone }: { sources: ResearchSourcesResult; researchDone: boolean }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const active = !researchDone;

  // Auto-scroll : balaye lentement les buckets pendant que le Conseil cherche,
  // pour que YouTube ne monopolise pas la vue et que Insta/TikTok/X soient visibles.
  useEffect(() => {
    if (!active) return;
    const panel = panelRef.current;
    if (!panel) return;
    const scroller = panel.closest(
      ".qv3-cockpit-sources, .qv3-cockpit-drawer",
    ) as HTMLElement | null;
    if (!scroller) return;
    let i = 0;
    const tick = () => {
      const buckets = panel.querySelectorAll<HTMLElement>(".qv3-bucket");
      if (!buckets.length) return;
      i = (i + 1) % buckets.length;
      const el = buckets[i];
      const r = el.getBoundingClientRect();
      const sr = scroller.getBoundingClientRect();
      scroller.scrollBy({ top: r.top - sr.top - 12, behavior: "smooth" });
    };
    const id = window.setInterval(tick, 4500);
    return () => window.clearInterval(id);
  }, [active]);

  return (
    <div className="qv3-sources-panel" ref={panelRef}>
      <h2 className="qv3-sources-eyebrow">
        <span>Recherches</span>
      </h2>
      <ul className="qv3-sources-list">
        {sources.buckets.map((b) => (
          <SourcesBucket key={b.platform} bucket={b} researchDone={researchDone} />
        ))}
      </ul>
      {active && (
        <div className="qv3-sources-footer">
          <div className="qv3-sources-footer-label">
            <span className="qv3-live-dot" aria-hidden="true" />
            <span>Recherches Live</span>
          </div>
          <ResearchTeleprompter active={active} />
        </div>
      )}
    </div>
  );
}

function ResearchTeleprompter({ active }: { active: boolean }) {
  const [phase, setPhase] = useState<0 | 1>(0);
  useEffect(() => {
    if (!active) {
      setPhase(0);
      return;
    }
    const t = window.setTimeout(() => setPhase(1), 15000);
    return () => window.clearTimeout(t);
  }, [active]);
  if (!active) return null;
  const text =
    phase === 0
      ? "Recherches en cours — le Conseil cherche les données pertinentes…"
      : "Instruire, c'est former le jugement — sans la bonne information, pas de verdict fiable.";
  return (
    <div className="qv3-prompter" aria-live="polite" aria-label="Statut des recherches">
      <div className="qv3-prompter-track" key={text}>
        <span className="qv3-prompter-text">{text}</span>
        <span className="qv3-prompter-text" aria-hidden="true">{text}</span>
      </div>
    </div>
  );
}

function SourcesBucket({ bucket, researchDone }: { bucket: PlatformBucket; researchDone: boolean }) {
  const isEmpty = bucket.count === 0;
  const [open, setOpen] = useState(true);
  return (
    <li className={`qv3-bucket${isEmpty ? " is-empty" : ""}`}>
      <button
        type="button"
        className="qv3-bucket-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="qv3-bucket-label">{bucket.label}</span>
        <span className="qv3-mono qv3-bucket-count">({bucket.count})</span>
        <span className="qv3-bucket-caret" aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      {open && (
        isEmpty ? (
          researchDone ? (
            <p className="qv3-bucket-empty">Aucune source</p>
          ) : (
            <ul className="qv3-bucket-items">
              <BucketSkeletonItem platform={bucket.platform} />
            </ul>
          )
        ) : (
          <ul className="qv3-bucket-items">
            {bucket.items.slice(0, 8).map((src) => (
              <SourceItem key={src.id} src={src} platform={bucket.platform} />
            ))}
          </ul>
        )
      )}
    </li>
  );
}

function BucketSkeletonItem({ platform }: { platform: PlatformBucket["platform"] }) {
  const isVisual = platform === "youtube" || platform === "tiktok" || platform === "instagram" || platform === "pinterest";
  const isScholar = platform === "scholar";
  const isLegal = platform === "juridique";
  return (
    <li className="qv3-bucket-item qv3-fade-in">
      <div className="qv3-bucket-link qv3-bucket-link-rich qv3-bucket-skeleton" aria-hidden="true">
        {isVisual ? (
          <span className="qv3-thumb qv3-thumb-skel">
            <span className="qv3-thumb-scan" />
          </span>
        ) : isScholar ? (
          <span className="qv3-thumb qv3-thumb-icon">◆</span>
        ) : isLegal ? (
          <span className="qv3-thumb qv3-thumb-icon qv3-thumb-legal">⚖</span>
        ) : (
          <span className="qv3-thumb qv3-thumb-doc">
            <span className="qv3-doc-line" />
            <span className="qv3-doc-line" />
            <span className="qv3-doc-line short" />
          </span>
        )}
        <span className="qv3-bucket-textwrap">
          <span className="qv3-bucket-title qv3-skel-line" />
          <span className="qv3-mono qv3-bucket-meta">En écoute du flux…</span>
        </span>
      </div>
    </li>
  );
}

/* FIX 5 — Item source avec thumbnail (YouTube + autres si dispo) */
function SourceItem({
  src,
  platform,
}: {
  src: InvestigationSource;
  platform: PlatformBucket["platform"];
}) {
  const isScholar = platform === "scholar";
  const isLegal = platform === "juridique";
  const isVisual = platform === "youtube" || platform === "tiktok" || platform === "instagram" || platform === "pinterest";
  const thumb =
    src.thumbnail || (platform === "youtube" ? youtubeThumb(src.url) : null) || null;

  return (
    <li className="qv3-bucket-item qv3-fade-in">
      <a
        href={src.url}
        target="_blank"
        rel="noreferrer noopener"
        className="qv3-bucket-link qv3-bucket-link-rich"
        title={src.title}
      >
        {isVisual && thumb ? (
          <span className="qv3-thumb" aria-hidden="true">
            <img src={thumb} alt="" loading="lazy" />
          </span>
        ) : isScholar ? (
          <span className="qv3-thumb qv3-thumb-icon" aria-hidden="true">
            ◆
          </span>
        ) : isLegal ? (
          <span className="qv3-thumb qv3-thumb-icon qv3-thumb-legal" aria-hidden="true">
            ⚖
          </span>
        ) : null}
        <span className="qv3-bucket-textwrap">
          <span className="qv3-bucket-title">{src.title || src.url}</span>
          {src.metadata?.channel && (
            <span className="qv3-mono qv3-bucket-meta">{src.metadata.channel}</span>
          )}
        </span>
      </a>
    </li>
  );
}

/* ====================== Styles scopés cockpit ====================== */
function CockpitStyles() {
  return (
    <style>{`
      .qv3-cockpit {
        height: 100vh;
        height: 100dvh;
        display: flex;
        flex-direction: column;
        background: var(--qv3-ink);
        color: var(--qv3-paper);
        overflow: hidden;
      }
      .qv3-cockpit-header {
        flex: 0 0 auto;
        padding: 16px 28px;
        border-bottom: 1px solid rgba(255,255,255,0.06);
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        gap: 16px;
      }
      .qv3-cockpit-brand { justify-self: start; }
      .qv3-cockpit-brand .nanae-wordmark { color: var(--qv3-paper); }
      .qv3-cockpit-title {
        justify-self: center;
        font-family: var(--qv3-font-serif);
        font-weight: 300;
        font-size: 18px;
        letter-spacing: 0.02em;
        color: var(--qv3-paper);
      }
      .qv3-cockpit-meta {
        justify-self: end;
        display: flex;
        gap: 14px;
        align-items: center;
        flex-wrap: nowrap;
        white-space: nowrap;
        color: var(--qv3-grey-500);
      }
      .qv3-cockpit-dot-sep { color: var(--qv3-gold); }
      .qv3-cockpit-new-link {
        padding: 6px 12px;
        border: 1px solid #F5EFE3;
        color: #F5EFE3;
        text-decoration: none;
        letter-spacing: 0.08em;
        white-space: nowrap;
        line-height: 1.2;
      }
      /* ===== LIVE pill with white luminous halo ===== */
      .qv3-live-pill {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 6px 14px;
        border-radius: 999px;
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.18);
        white-space: nowrap;
        isolation: isolate;
      }
      .qv3-live-pill::before {
        content: "";
        position: absolute;
        inset: -10px;
        border-radius: 999px;
        background: radial-gradient(ellipse at center,
          rgba(255,255,255,0.55) 0%,
          rgba(255,255,255,0.22) 35%,
          rgba(255,255,255,0) 70%);
        filter: blur(14px);
        z-index: -1;
        animation: qv3-live-halo 2.6s ease-in-out infinite;
        pointer-events: none;
      }
      .qv3-live-pill-sep { color: rgba(255,255,255,0.45); }
      .qv3-live-pill-text { color: #F5EFE3; }
      @keyframes qv3-live-halo {
        0%, 100% { opacity: 0.55; transform: scale(0.96); }
        50%      { opacity: 1;    transform: scale(1.06); }
      }
      @media (prefers-reduced-motion: reduce) {
        .qv3-live-pill::before { animation: none; opacity: 0.7; }
      }
      @media (max-width: 720px) {
        .qv3-cockpit-header { grid-template-columns: 1fr auto; }
        .qv3-cockpit-title { display: none; }
        .qv3-cockpit-meta { flex-wrap: wrap; justify-content: flex-end; }
      }

      /* Lotus loader — empty state */
      .qv3-cockpit-lotus-loader {
        display: inline-flex;
        animation: qv3-lotus-breathe 3.4s ease-in-out infinite;
      }
      @keyframes qv3-lotus-breathe {
        0%, 100% { transform: scale(1); opacity: 0.9; }
        50%      { transform: scale(1.04); opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .qv3-cockpit-lotus-loader { animation: none; }
      }

      /* User intervention bubble — aligned right, gold on dark */
      .qv3-bubble-row.is-user {
        grid-template-columns: 1fr 32px;
        justify-items: end;
      }
      .qv3-bubble-row.is-user .qv3-bubble {
        background: var(--qv3-gold) !important;
        color: var(--qv3-ink) !important;
        border: 1px solid var(--qv3-gold) !important;
        border-radius: 8px !important;
        box-shadow: 0 0 12px rgba(255,23,68,0.25) !important;
        justify-self: end;
      }
      .qv3-bubble-row.is-user .qv3-bubble-name {
        text-align: right;
        font-style: normal;
        font-family: var(--qv3-font-mono);
        font-size: 10px;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--qv3-ink);
        opacity: 0.7;
      }
      .qv3-bubble-row.is-user .qv3-bubble-text { color: var(--qv3-ink); }
      .qv3-bubble-avatar-user {
        background: var(--qv3-grey-900);
        border: 1px solid var(--qv3-gold);
        color: var(--qv3-gold);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: var(--qv3-font-mono);
        font-size: 11px;
        letter-spacing: 0.05em;
      }


      .qv3-cockpit-grid {
        flex: 1 1 auto;
        display: grid;
        grid-template-columns: 1fr;
        min-height: 0;
        overflow: hidden;
      }
      @media (min-width: 900px) {
        .qv3-cockpit-grid {
          grid-template-columns: 280px 1fr 280px;
        }
      }

      /* === Cadres === */
      .qv3-cockpit-cadres {
        border-right: 1px solid rgba(255,255,255,0.06);
        padding: 10px 28px 12px;
        overflow-y: auto;
        overflow-x: visible;
      }
      .qv3-cockpit-aside-title {
        font-family: var(--qv3-font-serif);
        font-weight: 400;
        font-size: 13px;
        letter-spacing: 0.02em;
        color: var(--qv3-grey-300);
        margin: 0 0 10px;
      }
      .qv3-cadre-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .qv3-cadre {
        display: grid;
        grid-template-columns: 40px 1fr 8px;
        align-items: center;
        gap: 12px;
        padding: 4px 4px;
        transition: opacity 240ms var(--qv3-ease);
        opacity: 0.6;
      }
      .qv3-cadre.is-active { opacity: 1; }

      /* FIX 6 — portrait + halo doré sur cadre actif */
      .qv3-cadre-photo {
        width: 40px;
        height: 50px;
        overflow: hidden;
        background: #111;
        filter: grayscale(100%) contrast(1.1);
        border: 1px solid rgba(255,255,255,0.12);
        transform: scale(1);
        transition: transform 400ms var(--qv3-ease),
                    border-color 400ms var(--qv3-ease),
                    box-shadow 400ms var(--qv3-ease);
      }
      .qv3-cadre-photo img { width: 100%; height: 100%; object-fit: cover; }

      .qv3-cadre.is-active { z-index: 10; position: relative; }
      .qv3-cadre.is-active .qv3-cadre-photo {
        border: 2px solid var(--qv3-gold);
        transform: scale(1.10);
        box-shadow:
          0 0 24px 8px rgba(255,23,68,0.70),
          0 0 48px 16px rgba(255,23,68,0.40),
          0 0 80px 24px rgba(255,23,68,0.20);
        animation: qv3-halo-pulse-strong 1.8s ease-in-out infinite;
      }
      @keyframes qv3-halo-pulse-strong {
        0%, 100% {
          box-shadow:
            0 0 24px 8px rgba(255,23,68,0.70),
            0 0 48px 16px rgba(255,23,68,0.40),
            0 0 80px 24px rgba(255,23,68,0.20);
        }
        50% {
          box-shadow:
            0 0 32px 12px rgba(255,23,68,0.90),
            0 0 64px 24px rgba(255,23,68,0.60),
            0 0 96px 36px rgba(255,23,68,0.30);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .qv3-cadre.is-active .qv3-cadre-photo { animation: none; }
      }

      .qv3-cadre-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .qv3-cadre-name {
        font-family: var(--qv3-font-serif);
        font-size: 15px;
        color: var(--qv3-paper);
        letter-spacing: -0.01em;
      }
      .qv3-cadre-role {
        font-family: var(--qv3-font-sans);
        font-size: 10px;
        color: #F5EFE3;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Mobile: dots horizontaux à la place de la liste */
      .qv3-cadre-dots-mobile {
        display: none;
        gap: 14px;
        justify-content: center;
        padding: 4px 0 0;
      }
      @media (max-width: 899px) {
        .qv3-cockpit-cadres {
          border-right: none;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          padding: 14px 20px;
          overflow: visible;
        }
        .qv3-cockpit-aside-title { display: none; }
        .qv3-cadre-list { display: none; }
        .qv3-cadre-dots-mobile { display: flex; }
      }

      /* === Invité (8e siège) — convive, pas cadre permanent === */
      .qv3-cadre-guests {
        margin-top: 14px;
        padding-top: 14px;
        border-top: 1px dashed rgba(212, 165, 116, 0.25);
      }
      .qv3-cadre-guest .qv3-cadre-photo-guest {
        position: relative;
        outline: 1px dashed var(--qv3-guest-accent, rgba(212, 165, 116, 0.55));
        outline-offset: 2px;
      }
      .qv3-cadre-guest-badge {
        position: absolute;
        left: 50%;
        bottom: -8px;
        transform: translateX(-50%);
        background: var(--qv3-ink, #0e0e0e);
        color: var(--qv3-guest-accent, #d4a574);
        font-family: "DM Mono", monospace;
        font-size: 8px;
        letter-spacing: 1.4px;
        text-transform: uppercase;
        padding: 2px 6px;
        border: 1px solid var(--qv3-guest-accent, rgba(212, 165, 116, 0.6));
        white-space: nowrap;
        pointer-events: none;
      }
      .qv3-dot-guest { outline: 1px dashed rgba(212, 165, 116, 0.5); outline-offset: 1px; }
      .qv3-bubble-row.is-guest .qv3-bubble-avatar-guest {
        outline: 1px dashed var(--qv3-guest-accent, rgba(212, 165, 116, 0.55));
        outline-offset: 2px;
        position: relative;
      }
      .qv3-bubble-avatar-guest-badge {
        position: absolute;
        left: 50%;
        bottom: -10px;
        transform: translateX(-50%);
        background: var(--qv3-ink, #0e0e0e);
        color: var(--qv3-guest-accent, #d4a574);
        font-family: "DM Mono", monospace;
        font-size: 7.5px;
        letter-spacing: 1.4px;
        text-transform: uppercase;
        padding: 1px 5px;
        border: 1px solid var(--qv3-guest-accent, rgba(212, 165, 116, 0.6));
        white-space: nowrap;
        pointer-events: none;
      }


      /* === Stream === */
      .qv3-cockpit-stream {
        overflow-y: auto;
        padding: 32px 24px 32px;
        background: var(--qv3-ink);
        display: flex;
        flex-direction: column;
      }
      @media (min-width: 900px) {
        .qv3-cockpit-stream { padding: 48px 64px 32px; }
      }
      .qv3-cockpit-empty {
        max-width: 480px;
        margin: 80px auto 0;
        text-align: center;
      }
      .qv3-bubble-stream {
        list-style: none;
        padding: 0;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 18px;
        max-width: 760px;
        width: 100%;
      }

      /* FIX 3+4 — Avatar mini + WhatsApp layout */
      .qv3-bubble-row {
        display: grid;
        grid-template-columns: 32px minmax(0, 1fr);
        gap: 12px;
        align-items: flex-start;
      }
      .qv3-bubble-row.is-grouped { margin-top: -12px; }
      .qv3-bubble-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        overflow: hidden;
        background: transparent;
        flex-shrink: 0;
      }
      .qv3-bubble-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
        border: 1px solid rgba(255,23,68,0.4);
        filter: grayscale(100%) contrast(1.1);
        display: block;
      }
      .qv3-bubble-row .qv3-bubble {
        background: #FFFFFF !important;
        color: var(--qv3-ink) !important;
        border: 1px solid var(--qv3-gold) !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        max-width: 70% !important;
        box-shadow: none !important;
        justify-self: start;
      }
      .qv3-bubble-row .qv3-bubble-name {
        font-family: var(--qv3-font-serif);
        font-style: italic;
        font-size: 13px;
        color: var(--qv3-gold);
        margin: 0 0 4px;
      }
      .qv3-bubble-row .qv3-bubble-text {
        font-family: var(--qv3-font-sans);
        font-size: 15px;
        line-height: 1.55;
        margin: 0;
        color: var(--qv3-ink);
      }
      /* Alternance gauche/droite — conseillers et user (WhatsApp-like) */
      .qv3-bubble-row.is-right {
        grid-template-columns: minmax(0, 1fr) 32px;
      }
      .qv3-bubble-row.is-right .qv3-bubble {
        justify-self: end;
      }
      .qv3-bubble-row.is-right .qv3-bubble-name {
        text-align: right;
      }
      @media (max-width: 720px) {
        .qv3-bubble-row .qv3-bubble { max-width: 85% !important; }
      }

      /* FIX 1+2 — Input intervention sticky en bas du stream */
      .qv3-intervene {
        position: sticky;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 5;
        margin: 28px -24px 0;
        padding: 16px 24px;
        background: linear-gradient(to top, var(--qv3-ink) 70%, rgba(0,0,0,0));
        border-top: 1px solid var(--qv3-gold-soft);
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 10px;
        align-items: stretch;
      }
      @media (min-width: 900px) {
        .qv3-intervene { margin: 28px -64px 0; padding: 16px 64px; }
      }
      .qv3-intervene-input {
        background: var(--qv3-ink);
        color: var(--qv3-paper);
        border: 1px solid var(--qv3-gold);
        font-family: var(--qv3-font-sans);
        font-size: 14px;
        padding: 12px 16px;
        outline: none;
        border-radius: 0;
        transition: border-color 200ms var(--qv3-ease), box-shadow 200ms var(--qv3-ease);
      }
      .qv3-intervene-input::placeholder { color: var(--qv3-grey-500); }
      .qv3-intervene-input:focus {
        border-color: var(--qv3-gold);
        box-shadow: 0 0 0 2px rgba(255,23,68,0.25);
      }
      .qv3-intervene-input:disabled { opacity: 0.5; }
      .qv3-intervene-btn {
        background: var(--qv3-gold);
        color: var(--qv3-ink);
        padding: 12px 22px;
      }
      .qv3-intervene-btn:hover,
      .qv3-intervene-btn:focus-visible { background: var(--qv3-paper); color: var(--qv3-ink); }
      .qv3-intervene-btn:focus-visible {
        outline: 2px solid var(--qv3-paper);
        outline-offset: 3px;
        box-shadow: 0 0 0 4px rgba(255,23,68,0.45);
      }
      .qv3-intervene-btn:disabled { opacity: 0.4; cursor: not-allowed; }

      /* FIX 2 — Fin de débat */
      .qv3-debate-end {
        margin: 36px auto 8px;
        max-width: 760px;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
        text-align: center;
      }
      .qv3-debate-end-rule {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 14px;
        color: var(--qv3-grey-500);
      }
      .qv3-debate-end-rule::before,
      .qv3-debate-end-rule::after {
        content: "";
        flex: 1;
        height: 0;
        border-top: 1px solid var(--qv3-gold-soft);
      }
      .qv3-debate-end-rule .qv3-mono {
        font-size: 10px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--qv3-gold);
        white-space: nowrap;
      }
      .qv3-debate-end-cta {
        background: var(--qv3-gold);
        color: var(--qv3-ink);
        font-size: 15px;
        padding: 20px 40px;
        letter-spacing: 0.1em;
      }
      .qv3-debate-end-cta:hover,
      .qv3-debate-end-cta:focus-visible {
        background: var(--qv3-paper);
        color: var(--qv3-ink);
      }
      .qv3-debate-end-cta:focus-visible {
        outline: 2px solid var(--qv3-paper);
        outline-offset: 3px;
        box-shadow: 0 0 0 4px rgba(255,23,68,0.45);
      }
      .qv3-debate-end-resume {
        color: var(--qv3-grey-300);
        font-size: 12px;
      }

      /* === Sources === */
      .qv3-cockpit-sources {
        border-left: 1px solid rgba(255,255,255,0.06);
        padding: 24px 20px;
        overflow-y: auto;
      }
      .qv3-only-desktop { display: block; }
      .qv3-only-mobile { display: none; }
      @media (max-width: 899px) {
        .qv3-only-desktop { display: none; }
        .qv3-only-mobile { display: inline-flex; }
      }
      .qv3-sources-panel { display: flex; flex-direction: column; gap: 6px; }
      .qv3-sources-eyebrow {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
        font-family: var(--qv3-font-mono);
        font-weight: 400;
        font-size: 10px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--qv3-grey-500);
        margin: 0 0 4px;
        padding-bottom: 6px;
        border-bottom: 0.5px solid rgba(255,255,255,0.08);
      }
      .qv3-sources-eyebrow-status {
        font-size: 9px;
        letter-spacing: 0.12em;
        color: var(--qv3-gold-soft);
        text-transform: lowercase;
        font-style: italic;
        opacity: 0.8;
      }
      .qv3-sources-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
      .qv3-sources-footer {
        margin-top: 16px;
        padding-top: 10px;
        border-top: 0.5px solid rgba(255,255,255,0.10);
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;
      }
      .qv3-sources-footer-label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-family: var(--qv3-font-mono);
        font-size: 10px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--qv3-paper);
      }
      .qv3-live-dot {
        width: 6px; height: 6px; border-radius: 50%;
        background: #e74c3c;
        box-shadow: 0 0 8px rgba(231,76,60,0.8);
        animation: qv3-live-pulse 1.4s ease-in-out infinite;
      }
      @keyframes qv3-live-pulse {
        0%,100% { opacity: 1; }
        50% { opacity: 0.35; }
      }
      .qv3-prompter {
        width: 100%;
        max-width: 320px;
        overflow: hidden;
        white-space: nowrap;
        padding: 2px 0;
        font-family: var(--qv3-font-mono);
        font-size: 10px;
        letter-spacing: 0.06em;
        color: var(--qv3-paper);
        font-style: italic;
        text-align: right;
        opacity: 0.95;
      }
      .qv3-prompter-track {
        display: inline-flex;
        gap: 48px;
        animation: qv3-prompter-scroll 22s linear infinite;
        will-change: transform;
      }
      .qv3-prompter-text { display: inline-block; }
      @keyframes qv3-prompter-scroll {
        0%   { transform: translateX(0%); }
        100% { transform: translateX(-50%); }
      }
      @media (prefers-reduced-motion: reduce) {
        .qv3-prompter-track { animation: none; }
      }
      .qv3-bucket { border-top: 0.5px solid rgba(255,255,255,0.08); padding-top: 10px; }
      .qv3-bucket.is-empty .qv3-bucket-label { color: var(--qv3-paper); opacity: 0.9; }
      .qv3-bucket.is-empty .qv3-bucket-count { color: var(--qv3-gold-soft); opacity: 0.7; }
      .qv3-bucket.is-empty .qv3-bucket-head { cursor: pointer; }
      .qv3-bucket-empty {
        margin: 4px 0 0;
        padding: 0 0 4px;
        font-size: 10px;
        color: var(--qv3-grey-500);
        opacity: 0.6;
        letter-spacing: 0.04em;
      }
      .qv3-bucket-head {
        all: unset;
        cursor: pointer;
        display: flex;
        align-items: baseline;
        gap: 8px;
        width: 100%;
        padding: 4px 0;
      }
      .qv3-bucket-label {
        font-family: var(--qv3-font-serif);
        font-size: 13px;
        color: var(--qv3-paper);
        letter-spacing: 0.01em;
      }
      .qv3-bucket-count { color: var(--qv3-gold); font-size: 11px; }
      .qv3-bucket-caret {
        margin-left: auto;
        color: var(--qv3-grey-500);
        font-family: var(--qv3-font-mono);
        font-size: 14px;
      }
      .qv3-bucket-items {
        list-style: none;
        padding: 8px 0 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .qv3-bucket-item { line-height: 1.35; }
      .qv3-bucket-link {
        display: flex;
        flex-direction: column;
        gap: 2px;
        color: var(--qv3-grey-300);
        text-decoration: none;
        font-family: var(--qv3-font-mono);
        font-size: 11px;
        letter-spacing: 0.02em;
        padding: 4px 0;
        border-bottom: 0.5px solid transparent;
        transition: color 200ms var(--qv3-ease), border-color 200ms var(--qv3-ease);
      }
      .qv3-bucket-link:hover {
        color: var(--qv3-paper);
        border-bottom-color: var(--qv3-gold-soft);
      }

      /* FIX 5 — Layout enrichi avec thumbnail */
      .qv3-bucket-link-rich {
        flex-direction: row;
        gap: 10px;
        align-items: flex-start;
        padding: 6px 0;
      }
      .qv3-thumb {
        flex-shrink: 0;
        width: 80px;
        height: 45px;
        overflow: hidden;
        background: #111;
        border: 0.5px solid rgba(255,23,68,0.4);
        display: block;
        transition: transform 240ms var(--qv3-ease), border-color 240ms var(--qv3-ease);
      }
      .qv3-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .qv3-bucket-link-rich:hover .qv3-thumb {
        transform: scale(1.04);
        border-color: var(--qv3-gold);
      }
      .qv3-thumb-icon {
        width: 16px;
        height: 16px;
        background: transparent;
        border: none;
        color: var(--qv3-gold);
        font-size: 14px;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-top: 2px;
      }
      /* Sprint LEGAL-SOURCES-VISIBLE — l'icône ⚖ juridique en rouge DA (vs nacré scholar). */
      .qv3-thumb-legal {
        color: rgb(255, 23, 68);
      }
      .qv3-bucket-textwrap {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        flex: 1;
      }
      .qv3-bucket-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
      }
      .qv3-bucket-meta { color: var(--qv3-grey-500); font-size: 10px; }

      /* === Skeleton "live en attente" pour buckets vides === */
      .qv3-bucket-skeleton { cursor: default; opacity: 0.85; }
      .qv3-thumb-skel {
        position: relative;
        background: linear-gradient(135deg, #141414 0%, #1c1c1c 50%, #141414 100%);
      }
      .qv3-thumb-scan {
        position: absolute; inset: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(255,23,68,0.18) 50%, transparent 100%);
        animation: qv3-scan 1.8s linear infinite;
      }
      @keyframes qv3-scan {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      .qv3-thumb-doc {
        display: flex; flex-direction: column; justify-content: center;
        gap: 5px; padding: 8px; background: #0f0f0f;
      }
      .qv3-doc-line {
        height: 3px; background: rgba(255,23,68,0.28); width: 100%;
        animation: qv3-pulse 1.6s ease-in-out infinite;
      }
      .qv3-doc-line.short { width: 60%; }
      .qv3-skel-line {
        display: block; height: 10px; width: 70%;
        background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.10), rgba(255,255,255,0.04));
        background-size: 200% 100%;
        animation: qv3-shimmer 1.8s linear infinite;
      }
      @keyframes qv3-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes qv3-pulse {
        0%, 100% { opacity: 0.35; }
        50% { opacity: 0.75; }
      }
      .qv3-bucket.is-empty .qv3-bucket-label,
      .qv3-bucket.is-empty .qv3-bucket-count { opacity: 0.85; }

      /* === Footer === */
      .qv3-cockpit-footer {
        flex: 0 0 auto;
        padding: 14px 28px;
        border-top: 1px solid rgba(255,255,255,0.06);
        display: flex;
        gap: 24px;
        align-items: center;
        flex-wrap: wrap;
        font-family: var(--qv3-font-mono);
        color: var(--qv3-grey-500);
        font-size: 11px;
        letter-spacing: 0.04em;
      }
      .qv3-cockpit-sources-tap {
        margin-left: auto;
        all: unset;
        cursor: pointer;
        font-family: var(--qv3-font-mono);
        font-size: 11px;
        color: var(--qv3-gold);
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      /* === Drawer mobile === */
      .qv3-cockpit-drawer-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.7);
        z-index: 60;
        display: flex;
        justify-content: flex-end;
      }
      .qv3-cockpit-drawer {
        width: min(360px, 92vw);
        height: 100%;
        background: var(--qv3-ink);
        border-left: 1px solid var(--qv3-gold-soft);
        padding: 56px 20px 24px;
        overflow-y: auto;
        animation: qv3-fade-up 300ms var(--qv3-ease);
      }
      .qv3-cockpit-drawer-close {
        position: absolute;
        top: 12px;
        right: 16px;
        all: unset;
        cursor: pointer;
        font-size: 28px;
        color: var(--qv3-grey-300);
        line-height: 1;
      }
    `}</style>
  );
}

function CouncilWaiting() {
  const phrases = [
    "Les Sept Conseillers prennent place.",
    "Ils rassemblent les informations nécessaires.",
    "Chaque conseiller prépare son analyse.",
    "Le débat commencera dans un instant.",
  ];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % phrases.length), 3200);
    return () => clearInterval(t);
  }, [phrases.length]);

  return (
    <div className="qv3-cockpit-empty qv3-council-waiting">
      <style>{`
        .qv3-council-waiting .qv3-waiting-halo {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .qv3-council-waiting .qv3-waiting-halo::before {
          content: "";
          position: absolute;
          inset: -40%;
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(255,23,68, 0.45) 0%,
            rgba(255,23,68, 0.18) 35%,
            rgba(255,23,68, 0) 70%
          );
          filter: blur(8px);
          animation: qv3-waiting-pulse 3.2s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }
        .qv3-council-waiting .qv3-waiting-halo > * {
          position: relative;
          z-index: 1;
          animation: qv3-waiting-breath 3.2s ease-in-out infinite;
        }
        @keyframes qv3-waiting-pulse {
          0%, 100% { opacity: 0.55; transform: scale(0.92); }
          50%      { opacity: 1;    transform: scale(1.08); }
        }
        @keyframes qv3-waiting-breath {
          0%, 100% { filter: drop-shadow(0 0 6px rgba(255,23,68,0.35)); }
          50%      { filter: drop-shadow(0 0 18px rgba(255,23,68,0.7)); }
        }
        .qv3-council-waiting .qv3-waiting-rotator {
          position: relative;
          margin-top: 16px;
          min-height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .qv3-council-waiting .qv3-waiting-line {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Playfair Display', Georgia, serif;
          color: var(--qv3-grey-300);
          font-size: 19px;
          line-height: 1.55;
          margin: 0;
          opacity: 0;
          transform: translateY(8px);
          animation: qv3-waiting-cycle 3.2s ease-in-out;
        }
        @keyframes qv3-waiting-cycle {
          0%   { opacity: 0; transform: translateY(8px); }
          15%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
      `}</style>
      <div className="qv3-cockpit-lotus-loader qv3-waiting-halo" aria-hidden="true">
        <NanaeLogoSymbol size="xl" />
      </div>
      <p className="qv3-eyebrow" style={{ marginTop: 32 }}>— Le Conseil prend place</p>
      <div className="qv3-waiting-rotator" aria-live="polite">
        <p key={index} className="qv3-waiting-line">{phrases[index]}</p>
      </div>
    </div>
  );
}
